import { QueryClient, useMutation, useQueryClient } from 'react-query'
import { TrackedChangeComment } from 'types'
import { trackedChangeCommentsKeys } from './trackedChangeCommentsKeys'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { trackedChangesKeys } from '../trackedChanges/trackedChangesKeys'
import { buildErrorMessage } from 'utils'
import produce from 'immer'
import { TrackedChange } from 'types'
import { useArticlePassword } from 'hooks/useArticlePassword'

interface Props {
  articleKey: string
  layerId: string
  trackedChangeId: string
  id: string
  isRichText?: boolean
}

interface InputPayload {
  body: string
}

interface ResponseData {
  comment: TrackedChangeComment
}

export const useUpdateTrackedChangeComment = ({
  articleKey,
  layerId,
  trackedChangeId,
  id,
  isRichText = false,
}: Props) => {
  const queryClient = useQueryClient()
  const password = useArticlePassword(articleKey)

  return useMutation<ResponseData, Error, InputPayload>(
    trackedChangeCommentsKeys.update(trackedChangeId, id),
    async (payload) => {
      const response = await sendToApi<ResponseData>(
        `/articles/${articleKey}/layers/${layerId}/changes/${trackedChangeId}/comments/${id}`,
        {
          method: 'PUT',
          body: {
            ...payload,
            html_body: isRichText ? payload.body : null,
          },
        },
      )

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }

      return response.data
    },
    {
      onMutate: (variables) => {
        const currentTrackedChange = queryClient
          .getQueryData<CacheData>(trackedChangesKeys.list(layerId, { password }))
          ?.tracked_changes.find((trackedChange) => trackedChange.id === trackedChangeId)
        // Optimistic update
        updateCache(queryClient, password, layerId, trackedChangeId, id, variables)
        return currentTrackedChange?.comments.find((comment) => comment.id === id)
      },
      onError: (_error, _variables, oldAttributes) => {
        updateCache(queryClient, password, layerId, trackedChangeId, id, oldAttributes as InputPayload)
      },
      onSuccess: () => {
        queryClient.invalidateQueries(trackedChangesKeys.list(layerId, { password }))
      },
    },
  )
}

type CacheData = { tracked_changes: TrackedChange[] } | undefined

const updateCache = (
  queryClient: QueryClient,
  password: string | undefined,
  layerId: string,
  trackedChangeId: string,
  id: string,
  attributes: InputPayload,
) => {
  queryClient.setQueryData<CacheData>(trackedChangesKeys.list(layerId, { password }), (old) => {
    return produce(old, (draft) => {
      if (!draft) {
        return
      }
      const trackedChangeIndex = draft.tracked_changes.findIndex(
        (trackedChange) => trackedChange.id === trackedChangeId,
      )
      if (trackedChangeIndex !== -1) {
        const trackedChange = draft.tracked_changes[trackedChangeIndex]
        const commentIndex = trackedChange.comments.findIndex((comment) => comment.id === id)
        if (commentIndex !== -1) {
          trackedChange.comments[commentIndex] = { ...trackedChange.comments[commentIndex], ...attributes }
        }
      }
    })
  })
}
